import { assert } from '@ember/debug';
import { getOwner, setOwner } from '@ember/owner';
import { CacheHandler, Fetch, RequestManager } from '@warp-drive/core';
import { LegacyNetworkHandler } from '@warp-drive/legacy/compat';
import type { NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';

const DEFAULT_RENEW_THRESHOLD_MS = 1800000;
const DEFAULT_RENEWAL_COOLDOWN_MS = 5000;
const DEFAULT_RENEWAL_FAILURE_COOLDOWN_MS = 60000;

const defaultTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || '';
const defaultRequestId = () => crypto.randomUUID();

type TioRequestSession = {
  isAuthenticated?: boolean;
  data?: {
    authenticated?: {
      access_token?: string;
    };
  };
  expireAt?: number;
  renew?: (token?: string) => Promise<unknown> | unknown;
};

export type TioSessionRenewalOptions = {
  passToken?: boolean;
  thresholdMs?: number;
  cooldownMs?: number;
  failureCooldownMs?: number;
};

export type TioRequestManagerOptions = {
  apiKey: string;
  contentType: string;
  accept?: string;
  jsonApiKeyFormat?: string;
  sessionRenewal?: TioSessionRenewalOptions;
  timeZone?: () => string;
  requestId?: () => string;
};

class TioAuthRequestHandler {
  private nextRenewalAllowedAt = 0;

  constructor(private options: TioRequestManagerOptions) {}

  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;
    const headers = new Headers(request.headers);

    headers.append('Content-Type', this.options.contentType);
    if (this.options.accept) {
      headers.append('Accept', this.options.accept);
    }
    headers.append('x-api-key', this.options.apiKey);
    if (this.options.jsonApiKeyFormat) {
      headers.append('x-json-api-key-format', this.options.jsonApiKeyFormat);
    }
    headers.append('Time-Zone', this.timeZone());
    headers.append('X-Request-Id', this.requestId());

    const session = this.session;
    const accessToken = session.data?.authenticated?.access_token;
    if (session.isAuthenticated && accessToken) {
      headers.set('tio-auth-token', accessToken);
      this.maybeRenewSession();
    }

    return next({ ...request, headers });
  }

  private get session() {
    const owner = getOwner(this);
    assert('Expected owner to be available for request auth handler', owner);

    const session = owner.lookup('service:session');
    assert('Expected service:session to be registered', session);

    return session as TioRequestSession;
  }

  private timeZone() {
    return this.options.timeZone?.() ?? defaultTimeZone();
  }

  private requestId() {
    return this.options.requestId?.() ?? defaultRequestId();
  }

  private maybeRenewSession() {
    if (!this.options.sessionRenewal) {
      return;
    }

    const now = Date.now();
    const cooldownMs = this.options.sessionRenewal.cooldownMs ?? DEFAULT_RENEWAL_COOLDOWN_MS;
    if (now < this.nextRenewalAllowedAt) {
      return;
    }

    const session = this.session;
    const expiresAt = session.expireAt;
    if (!expiresAt) {
      return;
    }

    const timeLeft = expiresAt - now;
    const thresholdMs = this.options.sessionRenewal.thresholdMs ?? DEFAULT_RENEW_THRESHOLD_MS;

    if (timeLeft < thresholdMs && timeLeft > 0) {
      this.nextRenewalAllowedAt = now + cooldownMs;

      void (async () => {
        try {
          if (!session.isAuthenticated) {
            return;
          }

          const currentToken = session.data?.authenticated?.access_token;
          if (!currentToken) {
            return;
          }

          if (!session.renew) {
            throw new Error(
              'Session renewal is configured but service:session has no renew method'
            );
          }

          if (this.options.sessionRenewal?.passToken === false) {
            await session.renew();
          } else {
            await session.renew(currentToken);
          }
        } catch (error) {
          const failureCooldownMs =
            this.options.sessionRenewal?.failureCooldownMs ?? DEFAULT_RENEWAL_FAILURE_COOLDOWN_MS;
          this.nextRenewalAllowedAt = Date.now() + failureCooldownMs;
          console.error('Failed to renew session:', error);
        }
      })();
    }
  }
}

export function createTioRequestManager(ownedObject: object, options: TioRequestManagerOptions) {
  const owner = getOwner(ownedObject);
  assert('Expected owner to be available for request manager setup', owner);

  const authHandler = new TioAuthRequestHandler(options);
  setOwner(authHandler, owner);

  return new RequestManager()
    .use([LegacyNetworkHandler, authHandler, Fetch])
    .useCache(CacheHandler);
}
